import type { ReactNode } from 'react';
import React, { createContext, useContext, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { User, Role, AppConfig } from '../store/slices/authSlice';
import { clearAuthData, setAuthData } from '../store/slices/authSlice';
import type { RootState, AppDispatch } from '../store/store';

interface AuthContextType {
  user: User | null;
  role: Role[]| null;
  appConfig: AppConfig[]| null;
  setUser: (user: User | null) => void;
  login: (username: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user, tableauToken, token, role, appConfig } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    const storedToken = localStorage.getItem('tableauToken');
    const storedUserString = localStorage.getItem('user');
    const storedjwtToken = localStorage.getItem('jwtToken');
    const storedRoleString = localStorage.getItem('role');
    const storedappConfigString = localStorage.getItem('appConfig');
    if (storedToken !== null && storedUserString !== null && storedjwtToken !== null && storedRoleString != null && storedappConfigString != null) {
      try {
        const storedUser = JSON.parse(storedUserString) as User;
        const storedrole = JSON.parse(storedRoleString) as Role[];
        const storedappConfig = JSON.parse(storedappConfigString) as AppConfig[];

        dispatch(setAuthData({ tableauToken: storedToken, user: storedUser, token: storedjwtToken, role: storedrole, appConfig: storedappConfig }));
      } catch (error) {
        console.error('Failed to parse stored user data:', error);
      }
    }
  }, [dispatch]);

  const setUser = (newUser: User | null): void => {
    dispatch(setAuthData({ tableauToken, user: newUser, token,role, appConfig }));
  };

  const login = (username: string): void => {
    console.log(`Logged in with username: ${username}`);
  };

  const logout = (): void => {
    dispatch(clearAuthData());
    navigate('/');
  };

  const value = useMemo(
    () => ({
      user,
      setUser,
      login,
      logout,
      role,
      appConfig
    }),
    [user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
